import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Clear } from '@material-ui/icons'

import { strings } from '@constants'
import { Spacings, Text } from '@styles'
import { addRelationshipManagersToOrg, getAllEmployees } from '@data'
import { User } from '@data/model'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Select } from '@baseV2'

import { InputsWrapper } from '../styles'

const ManagerCard = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_3B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
`

const NameBlock = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: ${Spacings.SPACING_20B};

	& > b {
		font-size: ${Text.SMALL};
		color: ${({ theme }) => theme.colors.text.primary};
	}
	& > small {
		font-size: ${Text.EXTRA_SMALL};
		color: ${({ theme }) => theme.colors.text.secondary};
	}
`

const CommodityWrap = styled.div`
	min-width: ${Spacings.SPACING_32B};
`

const ClearIcon = styled(Clear)`
	cursor: pointer;
	color: ${({ theme }) => theme.colors.text.secondary};
`

const EditOrgManagers = ({ orgDetails, update }) => {
	const dispatch = useDispatch()
	const [employees, setEmployees] = useState([])
	const [commodityList] = useState(orgDetails?.commodity)
	const [selectedManagers, setSelectedManagers] = useState(orgDetails?.relationship_managers ?? [])

	const selectableEmployees = employees.filter((_e) => !_e.selected)

	useEffect(() => {
		dispatch(showLoader(true))
		getAllEmployees()
			.then((response) => {
				const list = response
					?.map((item) => new User(item)?.getUserDetails())
					?.map((_e) => ({ ..._e, display_name_number: _e.full_name ?? _e.mobile_number }))
				setEmployees(list)
			})
			.catch((error) => console.log(error))
			.finally(() => dispatch(showLoader(false)))
	}, [orgDetails])

	const onSelectManager = (selectedEmployee) => setSelectedManagers((_s) => [..._s, selectedEmployee])

	const makePrimary = (type, index) => {
		const _manager = selectedManagers[index]
		if (type === 'supply') _manager.is_primary_supply = true
		else _manager.is_primary = true
		selectedManagers[index] = _manager
		setSelectedManagers([...selectedManagers])
	}

	const removeHandler = (index) => setSelectedManagers((_s) => _s?.filter((_, idx) => idx !== index))

	const assignHandler = () => {
		dispatch(showLoader(true))
		addRelationshipManagersToOrg(orgDetails?.organisation_id, selectedManagers)
			.then(() => {
				update()
				dispatch(showToast(true, strings('msg_relationship_managers_edited_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				console.log(error)
				const erro_msg = error?.erro_msg ?? strings('msg_relationship_managers_edited_failed')
				dispatch(showToast(true, erro_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
				dispatch(toggleDrawer(false))
			})
	}

	const selectEmployeeCommodity = (commodity, index) =>
		setSelectedManagers((_data) => _data.map((item, idx) => (idx === index ? { ...item, commodity } : item)))

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<Select
						id='employee-select-v2'
						placeholder='Select Employee'
						label={strings('select_employee_s')}
						data={selectableEmployees}
						displayKey='display_name_number'
						primaryKey='user_id'
						value={{}}
						onChange={onSelectManager}
					/>
					{selectedManagers?.map((_employee, index) => (
						<ManagerCard key={`employee-${_employee?.user_id ?? index}`}>
							<NameBlock>
								<b>{_employee.full_name}</b>
								<small>{_employee.mobile_number}</small>
							</NameBlock>
							{!_employee?.is_primary && (
								<Button variant='outline' color='neutral' size='small' onClick={() => makePrimary('sales', index)}>
									{strings('sales')}
								</Button>
							)}
							{!_employee?.is_primary_supply && (
								<Button variant='outline' color='neutral' size='small' onClick={() => makePrimary('supply', index)}>
									{strings('supply')}
								</Button>
							)}
							{_employee?.is_primary && (
								<Button variant='ghost' color='primary' size='small' disabled>
									{strings('sales')}
								</Button>
							)}
							{_employee?.is_primary_supply && (
								<Button variant='ghost' color='primary' size='small' disabled>
									{strings('supply')}
								</Button>
							)}
							<CommodityWrap>
								<Select
									id={`select-commodity-${index}`}
									placeholder='Select Commodity'
									label='Commodity'
									small
									data={commodityList}
									primaryKey='commodity_id'
									displayKey='commodity_name'
									value={commodityList?.find((item) => item.commodity_id === _employee?.commodity?.commodity_id) ?? {}}
									onChange={(val) => selectEmployeeCommodity(val, index)}
								/>
							</CommodityWrap>
							<ClearIcon fontSize='small' onClick={() => removeHandler(index)} />
						</ManagerCard>
					))}
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button color='primary' onClick={assignHandler}>
					{strings('assign')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default EditOrgManagers
